export interface ResourceDefinitionItem {
  type: string;
  name: string;
  description: string;
  icon: string;
}

const items: ResourceDefinitionItem[] = [
  {
    type: "text/directory",
    name: "Folder",
    description: "Create a new folder",
    icon: "heroicons:folder",
  },
  {
    type: "application/vnd.pivotalgroup.resource-definition.report+json",
    name: "Report",
    description: "Create a new report",
    icon: "heroicons:presentation-chart-bar",
  },
  {
    type: "application/vnd.pivotalgroup.resource-definition.automation+json",
    name: "Automation",
    description: "Create a new automation",
    icon: "heroicons:rocket-launch",
  },
  {
    type: "application/vnd.pivotalgroup.resource-definition.page+json",
    name: "Page",
    description: "Create a new page",
    icon: "heroicons:window",
  },
  {
    type: "application/vnd.pivotalgroup.resource-definition.assessment+json",
    name: "Assessment",
    description: "Create a new assessment",
    icon: "heroicons:academic-cap",
  },
  {
    type: "application/vnd.pivotalgroup.agent+json",
    name: "Agent",
    description: "Create a new AI agent",
    icon: "heroicons:sparkles",
  },
  {
    type: "application/vnd.pivotalgroup.agent.flow+json",
    name: "Agentic Flow",
    description: "Create a new agentic flow",
    icon: "heroicons:sparkles",
  },
];

const editorRouteMap: Record<string, string> = {
  "application/vnd.pivotalgroup.agent+json": "agents/edit",
  "application/vnd.pivotalgroup.agent.flow+json": "agents/edit",
  "application/vnd.pivotalgroup.automation+json": "automations/edit",
  "application/vnd.pivotalgroup.automation+binary": "automations/edit",
  "application/vnd.pivotalgroup.theme+json": "theme/edit",
};

const viewRouteMap: Record<string, string> = {
  "application/vnd.pivotalgroup.data.assessment.answer+json": "data/preview",
};

function findByType(type?: string) {
  if (!type) return undefined;
  return items.find((i) => i.type === type);
}

function isResourceDefinitionType(type?: string) {
  if (!type?.startsWith("application/vnd.pivotalgroup.resource-definition")) {
    return false;
  }
  return !!findByType(type);
}

function getEditorRoute(type?: string) {
  if (!type) return undefined;
  if (isResourceDefinitionType(type)) return "resources/edit";
  return editorRouteMap[type];
}

function getViewRoute(type?: string) {
  if (!type) return undefined;
  return viewRouteMap[type];
}

function canOpenViewer(type?: string) {
  if (!type || !getViewRoute(type)) return false;

  const { profile } = useProfile();
  const { hasContentPermission, hasPermission, isInternalContentType } =
    usePermissions();
  const permissions = profile.value?.permissions;

  if (
    isInternalContentType(type) &&
    !hasPermission(permissions, "internal:admin")
  ) {
    return false;
  }

  return (
    hasPermission(permissions, "files:read") &&
    hasContentPermission(permissions, type, "read")
  );
}

function canOpenEditor(type?: string, _emailAddress?: string | null) {
  if (!type || !getEditorRoute(type)) return false;

  const { profile } = useProfile();
  const { hasContentPermission, hasPermission, isInternalContentType } =
    usePermissions();
  const permissions = profile.value?.permissions;

  if (
    isInternalContentType(type) &&
    !hasPermission(permissions, "internal:admin")
  ) {
    return false;
  }

  return hasContentPermission(permissions, type, "write");
}

function getFileEntryIcon<
  T extends { type: string; metadata?: Record<string, any> }
>(file: T) {
  if (file.metadata && file.metadata["display-icon"]) {
    return file.metadata["display-icon"];
  }

  const def = findByType(file.type);
  if (def) {
    return def.icon;
  }
  return "heroicons:document";
}

function createGetFileEntryName(activeSite: Ref<{ name: string } | null>) {
  return function getFileEntryName<T extends { name?: string; metadata?: Record<string, any> }>(
    file?: T | null
  ) {
    if (file?.metadata && file.metadata["display-name"]) {
      return file.metadata["display-name"];
    }

    if (file?.name && file.name.trim()) {
      return file.name;
    }

    return activeSite.value?.name ?? "";
  };
}

function calculateRelativeFolderPath(basePath: string, fullParent: string): string {
  const base = basePath.replace(/\/$/, "");
  const full = fullParent.replace(/\/$/, "");

  if (base === "") {
    return full;
  } else if (full === base) {
    return "";
  } else if (full.startsWith(base + "/")) {
    return full.slice(base.length + 1);
  }

  return full;
}

export function useResourceDefinitions() {
  const { site } = useSite();
  const getFileEntryName = createGetFileEntryName(site);

  return {
    items,
    findByType,
    isResourceDefinitionType,
    getEditorRoute,
    getViewRoute,
    canOpenViewer,
    canOpenEditor,
    getFileEntryIcon,
    getFileEntryName,
    calculateRelativeFolderPath,
  };
}
